// client/src/components/contratos/ContratoForm.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useContratoById, useMutateContratos } from '../../hooks/useContratos';
import { useClientes } from '../../hooks/useClientes';
import { useFornecedores } from '../../hooks/useFornecedores';
import { useTransportadores } from '../../hooks/useTransportadores';
import { useMyAccountDetails, useTenantI18n } from '../../hooks/useAccounts';
import { useServicos } from '../../hooks/useServicos';
import { useItensVenda } from '../../hooks/useItensVenda';
import { useRecursos } from '../../hooks/useRecursos';
import { useCursos } from '../../hooks/useCursos';
import { useOfertas } from '../../hooks/useOfertas';
import { useTemplatesAtivosPorTipo } from '../../hooks/useTemplateContrato';
import { useUnidades } from '../../hooks/useUnidades';
import useAlert from '../../hooks/useAlert';
import { Save, XCircle, Loader2, PlusCircle, Trash2 } from 'lucide-react';

const CONTRAPARTE_LABEL = { Cliente: 'Cliente', Fornecedor: 'Fornecedor', Transportador: 'Transportador' };

// Mesmo leque de tipos que Venda.ItemVendaRealizadaSchema/Pedido aceitam
// (exceto PacoteCliente, instância já vinculada a um cliente específico —
// não faz sentido como item de contrato). Ver server/models/Contrato.js:
// ItemContratadoSchema.modelo.
const TIPO_ITEM_LABEL = { Servico: 'Serviço', ItemVenda: 'Produto', Recurso: 'Recurso', Curso: 'Curso', Oferta: 'Pacote' };

const initialState = {
    numeroContrato: '',
    tipoContrato: '',
    template: '',
    modeloContraparte: 'Cliente',
    cliente: '',
    unidade: '',
    transportadorDesignado: { motoristaId: '', veiculoId: '' },
    itens: [],
    statusContrato: 'Em Elaboração',
    valorTotalContrato: 0,
    observacoesGerais: '',
};

const ContratoForm = ({ current, onCloseForm, onSaveSuccess, tiposContrato = [] }) => {
    const { addContrato, updateContrato } = useMutateContratos();
    // ✅ ACHADO: fmtMoney era fixo em BRL, sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();
    const fmtMoney = (v) => (Number(v) || 0).toLocaleString(locale, { style: 'currency', currency });
    const { data: contratoData, isLoading: isContratoLoading } = useContratoById(
        current?._id,
        { enabled: !!current?._id }
    );
    const { data: accountData } = useMyAccountDetails();
    const accountId = accountData?._id;

    const { setAlert } = useAlert();

    const [contrato, setContrato] = useState(initialState);
    const [selectedTemplateData, setSelectedTemplateData] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const isEditing = !!current;

    // --- Contraparte (Cliente / Fornecedor / Transportador) ---
    const { data: clientesData, isLoading: isLoadingClientes } = useClientes({ limit: 1000, populate: 'user' }, accountId);
    const { data: fornecedoresData, isLoading: isLoadingFornecedores } = useFornecedores({ limit: 1000 });
    const { data: transportadoresData, isLoading: isLoadingTransportadores } = useTransportadores({ limit: 1000 });

    const clientes = clientesData?.data || [];
    const fornecedores = fornecedoresData?.data || [];
    const transportadores = transportadoresData?.data || [];

    const contraparteOptions = useMemo(() => {
        if (contrato.modeloContraparte === 'Fornecedor') {
            return fornecedores.map(f => ({ id: f._id, label: f.nomeFantasia || f.razaoSocial || 'Fornecedor sem nome' }));
        }
        if (contrato.modeloContraparte === 'Transportador') {
            return transportadores.map(t => ({ id: t._id, label: t.nomeCompleto || t.razaoSocial || 'Transportador sem nome' }));
        }
        return clientes.map(c => ({ id: c._id, label: c.user?.name || 'Cliente sem nome' }));
    }, [contrato.modeloContraparte, clientes, fornecedores, transportadores]);

    const isLoadingContraparte = contrato.modeloContraparte === 'Fornecedor' ? isLoadingFornecedores
        : contrato.modeloContraparte === 'Transportador' ? isLoadingTransportadores
        : isLoadingClientes;

    // Transportador selecionado como contraparte (objeto completo, com
    // motoristas[]/veiculos[] embutidos) — alimenta os selects de
    // "designado" abaixo. Só relevante quando modeloContraparte==='Transportador'.
    const transportadorSelecionado = useMemo(() => {
        if (contrato.modeloContraparte !== 'Transportador' || !contrato.cliente) return null;
        return transportadores.find(t => t._id === contrato.cliente) || null;
    }, [contrato.modeloContraparte, contrato.cliente, transportadores]);

    const { data: unidadesData, isLoading: isLoadingUnidades } = useUnidades();
    const unidades = unidadesData?.data || [];

    // --- Itens do contrato: busca real por tipo (Servico/Produto/Recurso/Curso/Pacote) ---
    const [tipoItemSelecionado, setTipoItemSelecionado] = useState('Servico');
    const [itemBuscaSelecionado, setItemBuscaSelecionado] = useState('');

    const { data: servicosData, isLoading: isLoadingServicos } = useServicos({ account: accountId, limit: 1000, ativo: true });
    const { data: produtosData, isLoading: isLoadingProdutos } = useItensVenda({ limit: 1000, ativo: true });
    const { data: recursosData, isLoading: isLoadingRecursos } = useRecursos(accountId, { limit: 1000 });
    const { data: cursosData, isLoading: isLoadingCursos } = useCursos(accountId, { limit: 1000, ativo: true });
    const { data: ofertasData, isLoading: isLoadingOfertas } = useOfertas({ limit: 1000, ativo: true });

    const opcoesPorTipo = useMemo(() => ({
        Servico: (servicosData?.data || []).map(s => ({ id: s._id, label: s.nome, preco: s.valorFixo || 0, raw: s })),
        ItemVenda: (produtosData?.data || []).map(p => ({ id: p._id, label: p.nome, preco: p.precoMinimo ?? p.unidadesDetalhes?.[0]?.preco ?? 0, raw: p })),
        Recurso: (recursosData?.data || []).map(r => ({ id: r._id || r.id, label: r.nome, preco: r.valorPorHora || 0, raw: r })),
        Curso: (cursosData?.data || []).map(c => ({ id: c._id, label: c.nome, preco: c.valor || 0, raw: c })),
        Oferta: (ofertasData?.data || []).map(o => ({ id: o._id, label: o.nome, preco: o.valorFixo ?? o.valorTotalEstimado ?? 0, raw: o })),
    }), [servicosData, produtosData, recursosData, cursosData, ofertasData]);

    const isLoadingItens = isLoadingServicos || isLoadingProdutos || isLoadingRecursos || isLoadingCursos || isLoadingOfertas;
    const opcoesDoTipoAtual = opcoesPorTipo[tipoItemSelecionado] || [];

    // Hook para buscar templates baseado no tipo de contrato selecionado
    const {
        data: availableTemplates = [],
        isLoading: isLoadingTemplates
    } = useTemplatesAtivosPorTipo(contrato.tipoContrato, !!contrato.tipoContrato);

    const loading = addContrato.isPending || updateContrato.isPending || isContratoLoading || isLoadingContraparte || isLoadingTemplates;

    // Carrega dados da edição — apenas uma vez
    useEffect(() => {
        if (isEditing && contratoData && isInitialLoad) {
            const contratoEditado = {
                ...initialState,
                ...contratoData,
                tipoContrato: contratoData.tipoContrato?._id || contratoData.tipoContrato || '',
                template: contratoData.template?._id || contratoData.template || '',
                modeloContraparte: contratoData.modeloContraparte || 'Cliente',
                cliente: contratoData.cliente?._id || contratoData.cliente || '',
                unidade: contratoData.unidade?._id || contratoData.unidade || '',
                transportadorDesignado: {
                    motoristaId: contratoData.transportadorDesignado?.motoristaId || '',
                    veiculoId: contratoData.transportadorDesignado?.veiculoId || '',
                },
            };

            setContrato(contratoEditado);
            setIsInitialLoad(false);
        } else if (!isEditing && isInitialLoad) {
            setContrato(initialState);
            setIsInitialLoad(false);
        }
    }, [contratoData, isEditing, isInitialLoad]);

    // Sincroniza template quando availableTemplates muda — sem resetar seleção válida
    useEffect(() => {
        if (contrato.template && availableTemplates.length > 0) {
            const template = availableTemplates.find(t => t._id === contrato.template);
            if (template) {
                setSelectedTemplateData(template);
            }
        } else if (contrato.tipoContrato && availableTemplates.length === 0 && !isLoadingTemplates) {
            setContrato(prev => ({ ...prev, template: '' }));
            setSelectedTemplateData(null);
        }
    }, [availableTemplates, contrato.template, contrato.tipoContrato, isLoadingTemplates]);

    // Recalcula valor total do contrato
    useEffect(() => {
        const total = (contrato.itens || []).reduce(
            (acc, item) => acc + (item.valorTotalItem || 0),
            0
        );
        setContrato((c) => ({ ...c, valorTotalContrato: total }));
    }, [contrato.itens]);

    const onChange = useCallback((e) => {
        const { name, value } = e.target;

        setContrato((prev) => {
            const newState = { ...prev, [name]: value };

            if (name === 'tipoContrato' && value !== prev.tipoContrato) {
                newState.template = '';
                setSelectedTemplateData(null);
            }

            if (name === 'template' && value) {
                const template = availableTemplates.find(t => t._id === value);
                setSelectedTemplateData(template || null);
            }

            return newState;
        });
    }, [availableTemplates]);

    const onSelectTipoContraparte = (tipo) => {
        setContrato(prev => ({ ...prev, modeloContraparte: tipo, cliente: '', transportadorDesignado: { motoristaId: '', veiculoId: '' } }));
    };

    const onChangeContraparte = (e) => {
        const { value } = e.target;
        setContrato(prev => ({ ...prev, cliente: value, transportadorDesignado: { motoristaId: '', veiculoId: '' } }));
    };

    const onChangeTransportadorDesignado = (field, value) => {
        setContrato(prev => ({ ...prev, transportadorDesignado: { ...prev.transportadorDesignado, [field]: value } }));
    };

    const handleItemChange = useCallback((index, field, value) => {
        setContrato((prev) => {
            const novosItens = [...prev.itens];
            novosItens[index] = { ...novosItens[index], [field]: value };
            novosItens[index].valorTotalItem =
                (Number(novosItens[index].quantidade) || 0) *
                (Number(novosItens[index].valorUnitarioAcordado) || 0);
            return { ...prev, itens: novosItens };
        });
    }, []);

    const adicionarItemDaBusca = useCallback(() => {
        if (!itemBuscaSelecionado) return setAlert('Selecione um item para adicionar.', 'warning');
        const found = opcoesDoTipoAtual.find(o => o.id === itemBuscaSelecionado);
        if (!found) return;

        setContrato((prev) => ({
            ...prev,
            itens: [
                ...(prev.itens || []),
                {
                    item: found.id,
                    modelo: tipoItemSelecionado,
                    descricaoItemNoContrato: found.label,
                    quantidade: 1,
                    valorUnitarioAcordado: found.preco,
                    valorTotalItem: found.preco,
                },
            ],
        }));
        setItemBuscaSelecionado('');
    }, [itemBuscaSelecionado, opcoesDoTipoAtual, tipoItemSelecionado, setAlert]);

    const removerItem = useCallback(
        (index) =>
            setContrato((prev) => ({
                ...prev,
                itens: prev.itens.filter((_, i) => i !== index),
            })),
        []
    );

    const onSubmit = (e) => {
        e.preventDefault();

        if (!contrato.cliente) {
            return setAlert(`Por favor, selecione um(a) ${CONTRAPARTE_LABEL[contrato.modeloContraparte].toLowerCase()}.`, 'warning');
        }
        if (!contrato.tipoContrato) {
            return setAlert('Por favor, selecione um tipo de contrato.', 'warning');
        }
        if (!contrato.template) {
            return setAlert('Por favor, selecione um template.', 'warning');
        }
        if (!selectedTemplateData) {
            return setAlert('Dados do template não encontrados. Por favor, selecione novamente.', 'warning');
        }
        if (!contrato.itens || contrato.itens.length === 0) {
            return setAlert('Adicione pelo menos um item ao contrato.', 'warning');
        }

        const contratoDataToSend = {
            numeroContrato: contrato.numeroContrato || undefined,
            tipoContrato: contrato.tipoContrato,
            template: contrato.template,
            cliente: contrato.cliente,
            modeloContraparte: contrato.modeloContraparte,
            unidade: contrato.unidade || undefined,
            transportadorDesignado: contrato.modeloContraparte === 'Transportador'
                ? {
                    motoristaId: contrato.transportadorDesignado.motoristaId || undefined,
                    veiculoId: contrato.transportadorDesignado.veiculoId || undefined,
                }
                : undefined,
            itens: contrato.itens.map(item => ({
                item: item.item,
                modelo: item.modelo,
                descricaoItemNoContrato: item.descricaoItemNoContrato,
                quantidade: Number(item.quantidade) || 1,
                valorUnitarioAcordado: Number(item.valorUnitarioAcordado) || 0,
                valorTotalItem: Number(item.valorTotalItem) || 0,
            })),
            statusContrato: contrato.statusContrato,
            valorTotalContrato: Number(contrato.valorTotalContrato) || 0,
            observacoesGerais: contrato.observacoesGerais || '',
        };

        if (contratoDataToSend.tipoContrato !== selectedTemplateData?.tipoContrato?._id) {
            return setAlert('Erro de compatibilidade entre template e tipo de contrato. Por favor, recarregue a página e tente novamente.', 'danger');
        }

        if (isEditing) {
            updateContrato.mutate(
                { id: current._id, contratoData: contratoDataToSend },
                { onSuccess: onSaveSuccess }
            );
        } else {
            addContrato.mutate(contratoDataToSend, { onSuccess: onSaveSuccess });
        }
    };

    const handleClose = () => {
        setIsInitialLoad(true);
        onCloseForm();
    };

    if (isEditing && isContratoLoading) {
        return (
            <div className="panel-state-wrapper">
                <Loader2 className="animate-spin" /> Carregando dados do contrato...
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="form-layout">
                <form onSubmit={onSubmit} noValidate>
                    <div className="form-header">
                        <h3 className="form-title">
                            {current
                                ? `Editar Contrato Nº ${contrato.numeroContrato}`
                                : 'Novo Contrato'}
                        </h3>
                        <button
                            type="button"
                            onClick={handleClose}
                            title="Fechar"
                            className="form-close-button"
                        >
                            <XCircle size={24} />
                        </button>
                    </div>

                    <fieldset className="form-section" disabled={loading}>
                        <legend className="form-section-title">Dados Principais</legend>
                        <div
                            className="form-grid-container"
                            style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
                        >
                            <div className="form-group">
                                <label htmlFor="tipoContrato">Tipo de Contrato*</label>
                                <select
                                    id="tipoContrato"
                                    name="tipoContrato"
                                    value={contrato.tipoContrato || ''}
                                    onChange={onChange}
                                    required
                                    className="form-input"
                                >
                                    <option value="">Selecione</option>
                                    {tiposContrato.map((t) => (
                                        <option key={t._id} value={t._id}>
                                            {t.nome}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="template">Template*</label>
                                <select
                                    id="template"
                                    name="template"
                                    value={contrato.template || ''}
                                    onChange={onChange}
                                    required
                                    disabled={!contrato.tipoContrato || isLoadingTemplates}
                                    className="form-input"
                                >
                                    <option value="">
                                        {isLoadingTemplates
                                            ? 'Carregando templates...'
                                            : !contrato.tipoContrato
                                                ? 'Escolha um Tipo'
                                                : availableTemplates.length === 0
                                                    ? 'Nenhum template disponível'
                                                    : 'Selecione'
                                        }
                                    </option>
                                    {availableTemplates.map((t) => (
                                        <option key={t._id} value={t._id}>
                                            {t.nome} {t.isPadrao ? '(Padrão)' : ''}
                                        </option>
                                    ))}
                                </select>
                                {contrato.tipoContrato && availableTemplates.length === 0 && !isLoadingTemplates && (
                                    <div className="form-help-text text-warning">
                                        ⚠️ Nenhum template disponível para este tipo de contrato.
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="numeroContrato">Nº Contrato</label>
                                <input
                                    type="text"
                                    id="numeroContrato"
                                    name="numeroContrato"
                                    value={contrato.numeroContrato || ''}
                                    onChange={onChange}
                                    className="form-input"
                                    placeholder="Automático se vazio"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="modeloContraparte">Contraparte*</label>
                                <select
                                    id="modeloContraparte"
                                    name="modeloContraparte"
                                    value={contrato.modeloContraparte}
                                    onChange={(e) => onSelectTipoContraparte(e.target.value)}
                                    className="form-input"
                                >
                                    {Object.keys(CONTRAPARTE_LABEL).map((tipo) => (
                                        <option key={tipo} value={tipo}>{CONTRAPARTE_LABEL[tipo]}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group full-width-on-grid">
                                <label htmlFor="cliente">{CONTRAPARTE_LABEL[contrato.modeloContraparte]}*</label>
                                <select
                                    id="cliente"
                                    name="cliente"
                                    value={contrato.cliente || ''}
                                    onChange={onChangeContraparte}
                                    required
                                    className="form-input"
                                    disabled={isLoadingContraparte}
                                >
                                    <option value="">{isLoadingContraparte ? 'Carregando...' : 'Selecione'}</option>
                                    {contraparteOptions.map((o) => (
                                        <option key={o.id} value={o.id}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="unidade">Unidade (opcional)</label>
                                <select
                                    id="unidade"
                                    name="unidade"
                                    value={contrato.unidade || ''}
                                    onChange={onChange}
                                    className="form-input"
                                    disabled={isLoadingUnidades}
                                >
                                    <option value="">{isLoadingUnidades ? 'Carregando...' : 'Nenhuma'}</option>
                                    {unidades.map((u) => (
                                        <option key={u._id} value={u._id}>{u.nome}</option>
                                    ))}
                                </select>
                            </div>

                            {contrato.modeloContraparte === 'Transportador' && transportadorSelecionado && (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="motoristaDesignado">Motorista Designado (opcional)</label>
                                        <select
                                            id="motoristaDesignado"
                                            value={contrato.transportadorDesignado.motoristaId || ''}
                                            onChange={(e) => onChangeTransportadorDesignado('motoristaId', e.target.value)}
                                            className="form-input"
                                        >
                                            <option value="">Nenhum</option>
                                            {(transportadorSelecionado.motoristas || []).map((m) => (
                                                <option key={m._id} value={m._id}>{m.nomeCompleto} — CPF {m.cpf}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="veiculoDesignado">Veículo Designado (opcional)</label>
                                        <select
                                            id="veiculoDesignado"
                                            value={contrato.transportadorDesignado.veiculoId || ''}
                                            onChange={(e) => onChangeTransportadorDesignado('veiculoId', e.target.value)}
                                            className="form-input"
                                        >
                                            <option value="">Nenhum</option>
                                            {(transportadorSelecionado.veiculos || []).map((v) => (
                                                <option key={v._id} value={v._id}>{v.modelo} — placa {v.placa}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>
                    </fieldset>

                    <fieldset className="form-section" disabled={loading}>
                        <legend className="form-section-title">Itens do Contrato</legend>

                        <div className="form-grid-container" style={{ gridTemplateColumns: '1fr 2fr 1fr' }}>
                            <div className="form-group">
                                <label htmlFor="tipoItemSelect">Tipo de Item</label>
                                <select
                                    id="tipoItemSelect"
                                    value={tipoItemSelecionado}
                                    onChange={(e) => { setTipoItemSelecionado(e.target.value); setItemBuscaSelecionado(''); }}
                                    className="form-input"
                                >
                                    {Object.keys(TIPO_ITEM_LABEL).map((tipo) => (
                                        <option key={tipo} value={tipo}>{TIPO_ITEM_LABEL[tipo]}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="itemBusca">Buscar {TIPO_ITEM_LABEL[tipoItemSelecionado]}</label>
                                <select
                                    id="itemBusca"
                                    value={itemBuscaSelecionado}
                                    onChange={(e) => setItemBuscaSelecionado(e.target.value)}
                                    className="form-input"
                                    disabled={isLoadingItens}
                                >
                                    <option value="">{isLoadingItens ? 'Carregando...' : 'Selecione'}</option>
                                    {opcoesDoTipoAtual.map((o) => (
                                        <option key={o.id} value={o.id}>
                                            {o.label} — {fmtMoney(o.preco)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group" style={{ alignSelf: 'end' }}>
                                <button type="button" onClick={adicionarItemDaBusca} className="add-item-button">
                                    <PlusCircle size={16} className="icon" /> Adicionar
                                </button>
                            </div>
                        </div>

                        <div className="dynamic-list-container">
                            {contrato.itens && contrato.itens.length > 0 && (
                                <div className="dynamic-list-item-header">
                                    <div className="col-desc">Descrição</div>
                                    <div className="col-qtd">Qtd.</div>
                                    <div className="col-valor">Valor Unit.</div>
                                    <div className="col-total">Total</div>
                                    <div className="col-action"></div>
                                </div>
                            )}

                            {contrato.itens.map((item, index) => (
                                <div key={index} className="dynamic-list-item">
                                    <div className="form-group col-desc">
                                        <input
                                            type="text"
                                            value={item.descricaoItemNoContrato || ''}
                                            onChange={(e) => handleItemChange(index, 'descricaoItemNoContrato', e.target.value)}
                                            className="form-input"
                                        />
                                        {!!item.modelo && (
                                            <div className="form-help-text">{TIPO_ITEM_LABEL[item.modelo] || item.modelo}</div>
                                        )}
                                    </div>
                                    <div className="form-group col-qtd">
                                        <input
                                            type="number"
                                            value={item.quantidade || 1}
                                            onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group col-valor">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.valorUnitarioAcordado || 0}
                                            onChange={(e) => handleItemChange(index, 'valorUnitarioAcordado', e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="col-total">
                                        {fmtMoney(item.valorTotalItem || 0)}
                                    </div>
                                    <div className="col-action">
                                        <button type="button" onClick={() => removerItem(index)} className="remove-item-button">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {(!contrato.itens || contrato.itens.length === 0) && (
                                <div className="form-help-text">Nenhum item adicionado ainda.</div>
                            )}
                        </div>

                        <div className="total-geral">
                            Total do Contrato:{' '}
                            <span>
                                {fmtMoney(contrato.valorTotalContrato)}
                            </span>
                        </div>
                    </fieldset>

                    <fieldset className="form-section" disabled={loading}>
                        <legend className="form-section-title">Observações</legend>
                        <div className="form-group full-width">
                            <label htmlFor="observacoesGerais">Observações Gerais</label>
                            <textarea
                                id="observacoesGerais"
                                name="observacoesGerais"
                                value={contrato.observacoesGerais || ''}
                                onChange={onChange}
                                rows="3"
                                className="form-input"
                            />
                        </div>
                    </fieldset>

                    <div className="form-actions">
                        <button type="button" onClick={handleClose} className="action-button action-button-secondary">
                            <XCircle className="icon" /> Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !contrato.tipoContrato || !contrato.template || !contrato.cliente || !selectedTemplateData}
                            className="action-button action-button-primary"
                        >
                            {loading ? <Loader2 className="animate-spin icon" /> : <Save className="icon" />}
                            Salvar Contrato
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContratoForm;
